/**
 * UtilityCodeService
 * Service for utility code generation (member codes, order codes, product codes, OTP)
 * Route: api/codes/utility
 * Controller: UtilityCodesController.cs
 */
import { BaseService } from '../base/BaseService';
import { ReturnType, } from '../../models/codegenerators';
/**
 * UtilityCodeService
 * Handles member codes, order codes, product codes, and OTP operations
 */
export class UtilityCodeService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? UtilityCodeService.DEFAULT_SERVICE_PATH,
            basePath: UtilityCodeService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== MEMBER CODES ====================
    /**
     * Generate a code for a member (QR or barcode)
     * POST api/codes/utility/member
     */
    async generateMemberCode(data) {
        const response = await this.generateMemberCodeRaw(data);
        if (!response.data) {
            throw new Error(response.message || 'Failed to generate member code');
        }
        return response.data;
    }
    /**
     * Generate member code with full response
     */
    generateMemberCodeRaw(data) {
        const endpoint = this.getEndpoint('member', 'member');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    // ==================== ORDER CODES ====================
    /**
     * Generate a code for an order
     * POST api/codes/utility/order/{orderId}
     */
    async generateOrderCode(orderId, asQrCode = true, returnType = ReturnType.File) {
        const response = await this.generateOrderCodeRaw(orderId, asQrCode, returnType);
        if (!response.data) {
            throw new Error(response.message || 'Failed to generate order code');
        }
        return response.data;
    }
    /**
     * Generate order code with full response
     */
    generateOrderCodeRaw(orderId, asQrCode = true, returnType = ReturnType.File) {
        const endpoint = this.getEndpoint('order', `order/${orderId}`);
        const url = this.buildUrl(endpoint);
        const params = new URLSearchParams({
            asQrCode: asQrCode.toString(),
            returnType: returnType,
        });
        return this.http.post(`${url}?${params.toString()}`, {});
    }
    // ==================== PRODUCT CODES ====================
    /**
     * Generate a code for a product
     * POST api/codes/utility/product/{productId}
     */
    async generateProductCode(productId, asQrCode = false, returnType = ReturnType.File) {
        const response = await this.generateProductCodeRaw(productId, asQrCode, returnType);
        if (!response.data) {
            throw new Error(response.message || 'Failed to generate product code');
        }
        return response.data;
    }
    /**
     * Generate product code with full response
     */
    generateProductCodeRaw(productId, asQrCode = false, returnType = ReturnType.File) {
        const endpoint = this.getEndpoint('product', `product/${productId}`);
        const url = this.buildUrl(endpoint);
        const params = new URLSearchParams({
            asQrCode: asQrCode.toString(),
            returnType: returnType,
        });
        return this.http.post(`${url}?${params.toString()}`, {});
    }
    // ==================== OTP OPERATIONS ====================
    /**
     * Generate an OTP (One-Time Password)
     * POST api/codes/utility/otp
     */
    async generateOtp(data) {
        const response = await this.generateOtpRaw(data);
        if (!response.data) {
            throw new Error(response.message || 'Failed to generate OTP');
        }
        return response.data;
    }
    /**
     * Generate OTP with full response
     */
    generateOtpRaw(data) {
        const endpoint = this.getEndpoint('otp', 'otp');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    /**
     * Validate an OTP
     * POST api/codes/utility/otp/validate
     */
    async validateOtp(data) {
        const response = await this.validateOtpRaw(data);
        if (!response.data) {
            throw new Error(response.message || 'Failed to validate OTP');
        }
        return response.data;
    }
    /**
     * Validate OTP with full response
     */
    validateOtpRaw(data) {
        const endpoint = this.getEndpoint('otp/validate', 'otp/validate');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    /**
     * Get remaining OTP attempts for an identifier
     * GET api/codes/utility/otp/{identifier}/attempts
     */
    async getOtpAttempts(identifier) {
        const response = await this.getOtpAttemptsRaw(identifier);
        return response.data ?? 0;
    }
    /**
     * Get OTP attempts with full response
     */
    getOtpAttemptsRaw(identifier) {
        const endpoint = this.getEndpoint('otp-attempts', `otp/${encodeURIComponent(identifier)}/attempts`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
UtilityCodeService.DEFAULT_SERVICE_PATH = 'codegenerators';
UtilityCodeService.BASE_PATH = 'api/codes/utility';
//# sourceMappingURL=UtilityCodeService.js.map